/**
 * EVAS v3 — auth-mode policy contracts and the LeadVal sign-in resolver (EVAS-137, Phase 2).
 *
 * Everything here is PURE and side-effect free: the blocking function, the console route handlers and
 * the tests all reach the same verdict from the same inputs. The architecture record, including every
 * decision encoded below, is docs/leadval/08_DUAL_MODE_IMPACT.md §9–§11 (approved 2026-08-29).
 *
 * Two invariants worth stating before reading the code:
 *  - The identity key is (provider, `sub`) — NEVER an email (§9.2). `User.leadvalSub` is that key.
 *  - The GCIP project baseline is `allowDuplicateEmails = true` (§9.9 item 1), so nothing links by
 *    itself: an *unlinked* LeadVal sign-in always arrives as a NEW uid, and a *deliberate* link always
 *    arrives as an existing uid that already carries local providers. `resolveLeadvalSignIn` relies on
 *    that distinction and would be unsound without the flip (§9.5).
 */
import { z } from "zod";
import { leadvalRoleToEvas } from "./leadval.js";
// ─────────────────────────── per-company auth policy (§9.6) ───────────────────────────
/** How a LeadVal identity may become attached to an existing EVAS account (§9.4 A vs B). */
export const LinkPolicy = z.enum(["EXPLICIT", "LINK_ON_LOGIN"]);
/** How long a session cookie lives — bounds how long an unnoticed LeadVal revocation survives (§10). */
export const SessionFreshness = z.enum(["FIVE_DAYS", "TWELVE_HOURS", "ONE_HOUR"]);
/** Whether (and how) LeadVal's `superadmin` role may yield EVAS `SUPER_ADMIN` (§9.11). Platform-wide:
 * a super admin has no Company, so there is no per-company row this could live on. All five values are
 * implemented (decision 2026-08-29); the last two are high-risk and labelled as such in the console. */
export const SuperAdminFederation = z.enum([
    "OFF", // no LeadVal path to SUPER_ADMIN at all
    "LINKED_ONLY", // default: the identity must already be linked to a SUPER_ADMIN row
    "INVITE", // a PENDING SUPER_ADMIN invite for that email authorises the projection
    "PROOF_OF_CONTROL", // any existing row may be elevated after linking (LeadVal role authoritative)
    "PROJECT_ANY", // first sign-in projects a SUPER_ADMIN row with no EVAS-side action
]);
export const CompanyAuthConfig = z.object({
    linkPolicy: LinkPolicy.optional(),
    /** Migration window (§9.6a): adopt an existing local User row onto the new LeadVal uid. */
    firstLinkAdoption: z.boolean().optional(),
    sessionFreshness: SessionFreshness.optional(),
});
/** The platform-wide row, `PlatformConfig("auth")` — same shape of preset as the geo-IP one. */
export const AuthPlatformConfig = z.object({
    defaults: CompanyAuthConfig.optional(),
    superAdminFederation: SuperAdminFederation.optional(),
});
/** The `PlatformConfig` key the global auth settings row is stored under. */
export const AUTH_PLATFORM_CONFIG_KEY = "auth";
export const DEFAULT_SUPER_ADMIN_FEDERATION = "LINKED_ONLY";
/** Mode defaults (§9.6): LeadVal-managed companies get a shorter session because LeadVal is
 * authoritative there and we can only learn about a revocation at sign-in (§10). */
export function authConfigModeDefaults(authSource) {
    return {
        linkPolicy: "EXPLICIT",
        firstLinkAdoption: false,
        sessionFreshness: authSource === "LEADVAL" ? "TWELVE_HOURS" : "FIVE_DAYS",
    };
}
/**
 * Resolve the effective policy: company override → platform defaults → mode default. Mirrors
 * `resolveGeoIpConfig` deliberately (one resolution pattern in the codebase, EVAS-138).
 *
 * Unparseable stored JSON is treated as ABSENT rather than fatal: a bad settings row must not be able
 * to lock every user of a company out of the console. The caller logs; the policy falls back.
 */
export function resolveAuthConfig(authSource, global, companyOverride) {
    const defaults = authConfigModeDefaults(authSource);
    const platform = AuthPlatformConfig.safeParse(global ?? {});
    const company = CompanyAuthConfig.safeParse(companyOverride ?? {});
    const p = platform.success ? (platform.data.defaults ?? {}) : {};
    const c = company.success ? company.data : {};
    return {
        linkPolicy: c.linkPolicy ?? p.linkPolicy ?? defaults.linkPolicy,
        firstLinkAdoption: c.firstLinkAdoption ?? p.firstLinkAdoption ?? defaults.firstLinkAdoption,
        sessionFreshness: c.sessionFreshness ?? p.sessionFreshness ?? defaults.sessionFreshness,
    };
}
/** The platform-level super-admin federation policy, defaulted. */
export function resolveSuperAdminFederation(global) {
    const parsed = AuthPlatformConfig.safeParse(global ?? {});
    return parsed.success
        ? (parsed.data.superAdminFederation ?? DEFAULT_SUPER_ADMIN_FEDERATION)
        : DEFAULT_SUPER_ADMIN_FEDERATION;
}
const SESSION_MS = {
    FIVE_DAYS: 5 * 24 * 60 * 60 * 1000,
    TWELVE_HOURS: 12 * 60 * 60 * 1000,
    ONE_HOUR: 60 * 60 * 1000,
};
/** Firebase session cookies accept 5 minutes – 14 days; every value above is legal, and the clamp is
 * here so a hand-edited settings row can never produce a cookie the Admin SDK refuses to mint. */
export function sessionCookieMs(freshness) {
    const ms = SESSION_MS[freshness] ?? SESSION_MS.FIVE_DAYS;
    return Math.min(Math.max(ms, 5 * 60 * 1000), 14 * 24 * 60 * 60 * 1000);
}
// ─────────────────────────── LeadVal UserInfo (§4.1, report 01 §1) ───────────────────────────
/** `GET /idp/userinfo` with an identity-plane access token and scope `workspace:read`. Tolerant of
 * unknown fields; `workspace` and `role` are absent unless the scope was granted — and absent means
 * DENY, never "assume". Shapes measured live on 2026-08-24 (EVAS-135). */
export const LeadvalUserInfo = z.object({
    sub: z.string().min(1),
    // ⚠ EVERY optional field is `nullish`, not `optional`. LeadVal emits explicit NULLS — read from
    // their source: `out.email = user.email ?? null`, `out.name = user.displayName ?? user.name ?? null`
    // (idp/provider.ts claims()) — and `z.string().optional()` accepts `undefined` while REJECTING
    // `null`, which fails the WHOLE object and cost a live sign-in with a misleading
    // "USERINFO_UNAVAILABLE". `leadval.ts` already carries this warning from when EVAS-129 hit it on the
    // lookup response; it is the same trap, one endpoint over. Only `sub` (and `workspace.id`, when a
    // workspace is present at all) is load-bearing enough to require.
    email: z.string().nullish(),
    email_verified: z.boolean().nullish(),
    name: z.string().nullish(),
    given_name: z.string().nullish(),
    family_name: z.string().nullish(),
    updated_at: z.number().nullish(),
    role: z.string().nullish(),
    role_rank: z.number().nullish(),
    workspace: z
        .object({
        id: z.string().min(1),
        name: z.string().nullish(),
        name_verified: z.boolean().nullish(),
        is_sub_workspace: z.boolean().nullish(),
        parent_id: z.string().nullish(),
    })
        .nullish(),
});
/** LeadVal's `error_reason` discriminators that mean "this person's access is gone" (report 01 §1,
 * guide §9.1). Any of them ends the local session and disables the projected row (§10). */
export const LEADVAL_REVOCATION_REASONS = [
    "grant_revoked",
    "user_left_tenant",
    "user_suspended",
    "user_missing",
    "tenant_suspended",
];
export function isLeadvalRevocation(reason) {
    return (typeof reason === "string" && LEADVAL_REVOCATION_REASONS.includes(reason));
}
// ─────────────────────────── the sign-in resolver (§9.3, §9.7, §9.11) ───────────────────────────
/** Refusal codes. These reach the browser inside the blocking function's error message (the only
 * channel GCIP gives us — it wraps the error as `auth/internal-error`), so they are PII-FREE and
 * STABLE: the console maps them to localized copy and, for `LINK_REQUIRED`, to the link-on-login
 * flow. Pinned by tests on both sides. */
export const LEADVAL_SIGNIN_REFUSALS = [
    "NO_CREDENTIAL", // GCIP forwarded no IdP access token — forwardInboundCredentials misconfigured
    "USERINFO_UNAVAILABLE", // LeadVal down/slow or scope missing — fail closed (report 01 §6.3)
    "REVOKED", // a LEADVAL_REVOCATION_REASONS discriminator
    "UNKNOWN_ROLE", // leadvalRoleToEvas returned null — fail closed, never downgrade
    "NO_WORKSPACE", // UserInfo carried no workspace (scope not granted)
    "EMAIL_ALREADY_LOCAL", // that email already belongs to an EVAS account — link, never project
    "PROJECTION_FAILED", // a projection write failed; refuse cleanly rather than throw a bare 503
    "NO_EMAIL", // UserInfo carried no email — `User.email` is NOT NULL + @unique, so a projected row
    // cannot be created without one, and "" would collide between two such users
    "COMPANY_LOCAL", // the workspace maps to a LOCAL company — LeadVal is not a login path there (E7)
    "LEADVAL_ONLY", // this account's company signs in with LeadVal ONLY — a local credential is refused
    "COMPANY_INACTIVE", // company suspended / pending
    "USER_INACTIVE", // the projected User row is not ACTIVE
    "LINK_REQUIRED", // an unlinked identity, and the company's policy is EXPLICIT
    "LINK_NOT_AUTHORIZED", // a link attempt with no live, fresh link-intent (§9.6a proof of control)
    "LINK_OFFERED", // an unlinked identity, LINK_ON_LOGIN — the console starts the prove-and-link flow
    "WORKSPACE_NOT_ONBOARDED", // unknown workspace and the signer is not an admin (E8)
    "SUPER_ADMIN_NOT_ALLOWED", // superAdminFederation refuses this path (§9.11)
    "ALREADY_LINKED_ELSEWHERE", // this sub is linked to a different User row (E13)
];
/** Message prefix the console matches on. Deliberately not localized and deliberately carries no
 * email, uid or workspace id — it is rendered into an error string we do not control. */
export const LEADVAL_REFUSAL_PREFIX = "EVAS_LEADVAL_";
export function refusalMessage(code) {
    return `${LEADVAL_REFUSAL_PREFIX}${code}`;
}
export function parseRefusal(message) {
    if (!message)
        return null;
    const m = message.match(new RegExp(`${LEADVAL_REFUSAL_PREFIX}([A-Z_]+)`));
    const code = m?.[1];
    return code && LEADVAL_SIGNIN_REFUSALS.includes(code)
        ? code
        : null;
}
function inactive(status) {
    return status !== "ACTIVE";
}
/** Would inserting a row for this identity collide with an existing account's email? A row already
 * keyed to THIS LeadVal subject is not a collision — that is the same person, reached through the
 * linked path rather than a projection. */
function emailTakenElsewhere(input) {
    const existing = input.userByEmailAnywhere;
    return !!existing && existing.leadvalSub !== input.userInfo.sub;
}
/**
 * The single decision function for a LeadVal sign-in. Total and fail-closed: every path ends in an
 * explicit verdict, and anything unrecognised refuses. Ordering matters and is pinned by tests —
 * cheap, non-disclosing checks first, exactly as LeadVal orders its own block reasons (§11.4).
 */
export function resolveLeadvalSignIn(input) {
    const { userInfo, userBySub, userByUid, company } = input;
    // 1. Role first: an unmapped role is refused, never downgraded (§9.11 "is a flow missing?").
    const mapped = userInfo.role ? leadvalRoleToEvas(userInfo.role) : null;
    if (!mapped)
        return { kind: "refuse", code: "UNKNOWN_ROLE" };
    // 2. Platform staff never touch the company machinery (§9.11 rule 1).
    if (mapped === "SUPER_ADMIN")
        return resolveSuperAdmin(input);
    // 3. A company user needs a workspace to resolve against.
    const workspaceId = userInfo.workspace?.id;
    if (!workspaceId)
        return { kind: "refuse", code: "NO_WORKSPACE" };
    // …and an email, because every path that may CREATE a row writes a NOT NULL, @unique column. Better
    // to refuse with a nameable reason than to insert "" and collide with the next such user.
    if (!userInfo.email)
        return { kind: "refuse", code: "NO_EMAIL" };
    // 4. An already-linked row wins immediately — this is the steady state.
    if (userBySub) {
        if (userBySub.firebaseUid !== input.uid) {
            // The sub is linked to a DIFFERENT account: an unlink+re-sign-in (E10) or a duplicate (E13).
            //
            // ⚠ REACHED ON SIGN-IN ONLY (EVAS-178, measured 2026-09-08). On the LINKING path GCIP refuses the
            // credential first — `FEDERATED_USER_ID_ALREADY_LINKED` inside `signInWithIdp`, surfacing as
            // `auth/credential-already-in-use` — and this function is never invoked; Cloud Logging held no entry
            // for the attempt. So this branch is correct and load-bearing for sign-in, and NOT the thing a user
            // sees when a link is refused for the same reason. The console maps that code onto this same copy
            // (`PROVIDER_REFUSALS` in apps/console/src/lib/auth-error.ts) so the two paths read alike. Do not
            // "fix" a link-path bug here: the refusal never gets this far.
            return { kind: "refuse", code: "ALREADY_LINKED_ELSEWHERE" };
        }
        if (inactive(userBySub.status))
            return { kind: "refuse", code: "USER_INACTIVE" };
        // A local SUPER_ADMIN is never re-roled by a LeadVal sign-in (§9.11 rule 2).
        if (userBySub.role === "SUPER_ADMIN") {
            if (input.superAdminFederation === "OFF") {
                return { kind: "refuse", code: "SUPER_ADMIN_NOT_ALLOWED" };
            }
            return {
                kind: "allow",
                userId: userBySub.id,
                role: "SUPER_ADMIN",
                companyId: null,
                syncRole: null,
                linkSub: false,
                adoptUid: false,
            };
        }
        if (!company)
            return { kind: "refuse", code: "WORKSPACE_NOT_ONBOARDED" };
        if (company.authSource === "LOCAL")
            return { kind: "refuse", code: "COMPANY_LOCAL" };
        if (inactive(company.status))
            return { kind: "refuse", code: "COMPANY_INACTIVE" };
        // Mode 4: LeadVal owns roles, so re-sync. Mode 3: ours are authoritative — claims-overwrite
        // guard, the whole point of SSO mode (§3 mode 3, Table A row A4).
        const syncRole = company.authSource === "LEADVAL" && userBySub.role !== mapped ? mapped : null;
        return {
            kind: "allow",
            userId: userBySub.id,
            role: syncRole ?? userBySub.role,
            companyId: userBySub.companyId,
            syncRole,
            linkSub: false,
            adoptUid: false,
        };
    }
    // 5. Not linked. A deliberate link is the only way a LeadVal identity reaches an existing uid.
    const isDeliberateLink = !input.isNewUser &&
        input.linkedProviderIds.some((p) => p !== input.leadvalProviderId) &&
        userByUid !== null;
    if (isDeliberateLink && userByUid) {
        // The console must have opened a link window moments ago against a freshly-issued token.
        if (!input.linkIntentValid)
            return { kind: "refuse", code: "LINK_NOT_AUTHORIZED" };
        if (inactive(userByUid.status))
            return { kind: "refuse", code: "USER_INACTIVE" };
        if (userByUid.leadvalSub && userByUid.leadvalSub !== userInfo.sub) {
            return { kind: "refuse", code: "ALREADY_LINKED_ELSEWHERE" };
        }
        if (userByUid.role === "SUPER_ADMIN") {
            // Linking LeadVal onto a super-admin account: allowed unless federation is OFF.
            if (input.superAdminFederation === "OFF") {
                return { kind: "refuse", code: "SUPER_ADMIN_NOT_ALLOWED" };
            }
            return {
                kind: "allow",
                userId: userByUid.id,
                role: "SUPER_ADMIN",
                companyId: null,
                syncRole: null,
                linkSub: true,
                adoptUid: false,
            };
        }
        if (!company)
            return { kind: "refuse", code: "WORKSPACE_NOT_ONBOARDED" };
        if (company.authSource === "LOCAL")
            return { kind: "refuse", code: "COMPANY_LOCAL" };
        if (inactive(company.status))
            return { kind: "refuse", code: "COMPANY_INACTIVE" };
        if (userByUid.companyId !== company.id) {
            // The local account belongs to a different company than the LeadVal workspace maps to.
            return { kind: "refuse", code: "COMPANY_LOCAL" };
        }
        return {
            kind: "allow",
            userId: userByUid.id,
            role: userByUid.role,
            companyId: userByUid.companyId,
            syncRole: null,
            linkSub: true,
            adoptUid: false,
        };
    }
    // 6. A brand-new, unlinked LeadVal identity. Everything now depends on the company's mode.
    if (!company) {
        // First contact with a workspace: only an admin projects a company (§9.7 E8).
        if (mapped !== "COMPANY_ADMIN") {
            return { kind: "refuse", code: "WORKSPACE_NOT_ONBOARDED" };
        }
        // Checked BEFORE the company is created, deliberately: a collision discovered after that insert
        // leaves a company with no members behind — which is what happened on dev 2026-08-30.
        if (emailTakenElsewhere(input))
            return { kind: "refuse", code: "EMAIL_ALREADY_LOCAL" };
        return { kind: "project", createCompany: true, role: mapped, workspaceId };
    }
    if (company.authSource === "LOCAL")
        return { kind: "refuse", code: "COMPANY_LOCAL" };
    if (inactive(company.status))
        return { kind: "refuse", code: "COMPANY_INACTIVE" };
    // 6a. A local row with the same verified email exists → this is a collision, not a new person.
    const emailMatch = input.emailMatchInCompany;
    if (emailMatch && !emailMatch.leadvalSub) {
        const verified = userInfo.email_verified === true;
        if (company.policy.firstLinkAdoption && verified && !inactive(emailMatch.status)) {
            // Migration window: adopt the row onto this uid and disable the old account (§9.6a).
            return {
                kind: "allow",
                userId: emailMatch.id,
                role: emailMatch.role,
                companyId: emailMatch.companyId,
                syncRole: company.authSource === "LEADVAL" && emailMatch.role !== mapped ? mapped : null,
                linkSub: true,
                adoptUid: true,
            };
        }
        // Otherwise the person must prove control of the local account first (§9.4 A/B).
        return {
            kind: "refuse",
            code: company.policy.linkPolicy === "LINK_ON_LOGIN" ? "LINK_OFFERED" : "LINK_REQUIRED",
        };
    }
    // 6b. Genuinely new person. Mode 4 projects them; mode 3 does not — membership is ours there, so
    // an unknown LeadVal identity has nothing to link to and must be invited the normal way.
    if (company.authSource === "LEADVAL") {
        if (emailTakenElsewhere(input))
            return { kind: "refuse", code: "EMAIL_ALREADY_LOCAL" };
        return { kind: "project", createCompany: false, role: mapped, workspaceId };
    }
    return { kind: "refuse", code: "LINK_REQUIRED" };
}
function resolveSuperAdmin(input) {
    const { userBySub, userByUid, superAdminFederation, userInfo } = input;
    if (superAdminFederation === "OFF") {
        return { kind: "refuse", code: "SUPER_ADMIN_NOT_ALLOWED" };
    }
    if (userBySub) {
        if (userBySub.firebaseUid !== input.uid) {
            return { kind: "refuse", code: "ALREADY_LINKED_ELSEWHERE" };
        }
        if (inactive(userBySub.status))
            return { kind: "refuse", code: "USER_INACTIVE" };
        if (userBySub.role !== "SUPER_ADMIN" && superAdminFederation !== "PROOF_OF_CONTROL") {
            // LINKED_ONLY / INVITE / PROJECT_ANY never elevate an existing company row.
            return { kind: "refuse", code: "SUPER_ADMIN_NOT_ALLOWED" };
        }
        return {
            kind: "allow",
            userId: userBySub.id,
            role: "SUPER_ADMIN",
            companyId: null,
            syncRole: userBySub.role === "SUPER_ADMIN" ? null : "SUPER_ADMIN",
            linkSub: false,
            adoptUid: false,
        };
    }
    // Not linked yet. A deliberate link onto an existing account is the LINKED_ONLY / PROOF_OF_CONTROL
    // path; INVITE and PROJECT_ANY may create a row from a fresh uid.
    const isDeliberateLink = !input.isNewUser &&
        input.linkedProviderIds.some((p) => p !== input.leadvalProviderId) &&
        userByUid !== null;
    if (isDeliberateLink && userByUid) {
        if (!input.linkIntentValid)
            return { kind: "refuse", code: "LINK_NOT_AUTHORIZED" };
        if (inactive(userByUid.status))
            return { kind: "refuse", code: "USER_INACTIVE" };
        const mayElevate = superAdminFederation === "PROOF_OF_CONTROL";
        if (userByUid.role !== "SUPER_ADMIN" && !mayElevate) {
            return { kind: "refuse", code: "SUPER_ADMIN_NOT_ALLOWED" };
        }
        return {
            kind: "allow",
            userId: userByUid.id,
            role: "SUPER_ADMIN",
            companyId: null,
            syncRole: userByUid.role === "SUPER_ADMIN" ? null : "SUPER_ADMIN",
            linkSub: true,
            adoptUid: false,
        };
    }
    // Only the PROJECTION branches need an email — they INSERT a row into a NOT NULL, @unique column, so
    // two emailless staff projections would collide. An already-linked super admin above creates nothing,
    // and refusing them for a null LeadVal email would break a sign-in that has every right to work.
    const mayProject = superAdminFederation === "PROJECT_ANY" ||
        (superAdminFederation === "INVITE" && input.hasPendingSuperAdminInvite);
    if (mayProject) {
        if (!userInfo.email)
            return { kind: "refuse", code: "NO_EMAIL" };
        if (emailTakenElsewhere(input))
            return { kind: "refuse", code: "EMAIL_ALREADY_LOCAL" };
        return { kind: "projectSuperAdmin" };
    }
    return { kind: "refuse", code: "SUPER_ADMIN_NOT_ALLOWED" };
}
/**
 * Guard for a NON-LeadVal sign-in (password, Google, Microsoft). Mode 4 forbids local credentials
 * forever — a live local credential after a LeadVal suspension is an authorization bypass (§8, guide
 * §9.1). Reads the persisted `authSource` custom claim so the fast path costs ZERO I/O, which matters:
 * once the trigger is registered this function runs for EVERY console sign-in (§9.7 E16).
 */
export function localSignInAllowed(claims) {
    const c = claims && typeof claims === "object" ? claims : {};
    // A SUPER_ADMIN is NEVER refused a local credential, whatever any other claim says. §9.9 item 6
    // calls that break-glass, and it has to be structural rather than a consequence of deriving
    // `authSource` correctly everywhere: a single bad write would otherwise lock platform staff out of
    // the console, and the way back in is the console. (A bad write is exactly what happened on
    // 2026-08-30 — a linked super admin was given `authSource: "LEADVAL"`.)
    if (c.role === "SUPER_ADMIN")
        return true;
    return c.authSource !== "LEADVAL";
}
/**
 * A NON-LeadVal sign-in that carries no EVAS claims at all — which is what `allowDuplicateEmails =
 * true` produces whenever someone signs in with Google/Microsoft using an address that already has an
 * account: GCIP mints a SEPARATE uid, and a separate uid has no claims (measured live 2026-09-01).
 *
 * `localSignInAllowed` cannot see this case — it only knows the claims on the uid in front of it, and
 * there are none — so §9.9 item 3 ("Mode 4 refuses every non-LeadVal sign-in") is NOT achievable with
 * zero I/O after all. It costs one lookup, and only on this path: an existing user always has claims
 * and never reaches here, so ordinary logins are still I/O-free.
 *
 * Legitimate reasons to be claimless: registering a new company, or accepting an invite (both set
 * claims immediately afterwards, and neither has a `User` row yet). Those must pass.
 */
export function resolveClaimlessLocalSignIn(input) {
    const row = input.existing;
    // No row = a genuinely new person (registration / invite acceptance). Let them in.
    if (!row)
        return { kind: "pass" };
    // Their OWN account, claims merely not written yet (mid-registration). Let them in.
    if (row.firebaseUid === input.uid)
        return { kind: "pass" };
    // Somebody already holds this email. If their company is LeadVal-managed, this IS the bypass §8
    // forbids — a second credential for the same person that outlives a LeadVal suspension.
    if (row.companyAuthSource === "LEADVAL")
        return { kind: "refuse", code: "LEADVAL_ONLY" };
    // Otherwise it is a duplicate account that would land in an empty console: refuse and say so.
    return { kind: "refuse", code: "EMAIL_ALREADY_LOCAL" };
}
/** Window a link-intent stays valid. Long enough for an OIDC popup round trip, short enough that an
 * abandoned intent is not a standing permission. */
export const LEADVAL_LINK_INTENT_MS = 5 * 60 * 1000;
/** How recently the caller must have authenticated for the console to open a link window. Firebase
 * ID tokens carry `auth_time`, so this is checkable server-side without any extra state. */
export const LEADVAL_LINK_MAX_AUTH_AGE_MS = 5 * 60 * 1000;
/** Is there a live link-intent on these persisted claims? */
export function linkIntentValid(claims, now = Date.now()) {
    const until = claims && typeof claims === "object"
        ? claims.leadvalLinkUntil
        : undefined;
    return typeof until === "number" && Number.isFinite(until) && until > now;
}
//# sourceMappingURL=auth-modes.js.map